import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../pages/app/protected/firebase';
import { collection, addDoc, getDocs, query, orderBy, where, Timestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { useContacts } from '../../../contexts/ContactContext';
import { FiPlus, FiSearch, FiCalendar, FiFilter, FiRefreshCw, FiDollarSign, FiEdit2, FiTrash2, FiUserPlus } from 'react-icons/fi';
import { format, startOfDay, endOfDay } from 'date-fns';
import { Combobox, Transition } from '@headlessui/react';

const MoneyIn = () => {
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const { contacts, addContact } = useContacts();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [contactSearch, setContactSearch] = useState('');
  
  const [filters, setFilters] = useState({
    startDate: today,
    endDate: today,
    reference: '',
  });

  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    reference: '',
    description: '',
    amount: '',
    category: 'revenue',
    paymentMethod: 'cash',
    account: 'cash',
    contact: null, // This will store the full contact object
  });

  const filteredContacts = React.useMemo(() => {
    if (!contacts) return [];
    
    const searchTerm = contactSearch.toLowerCase();
    
    return contacts.filter(contact => {
      if (!searchTerm) return true;
      
      return (
        contact.name?.toLowerCase().includes(searchTerm) ||
        (contact.company?.toLowerCase().includes(searchTerm)) ||
        (contact.email?.toLowerCase().includes(searchTerm)) ||
        (contact.phone?.includes(contactSearch))
      );
    });
  }, [contacts, contactSearch]);

  // Format currency as MYR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Fetch money in transactions
  const fetchTransactions = async () => {
    if (!user?.uid || !companyId) return;
    
    setLoading(true);
    try {
      let q = query(
        collection(db, 'companies', companyId, 'moneyIn'),
        orderBy('date', 'desc')
      );
      
      // Apply date filters if set
      if (filters.startDate || filters.endDate) {
        const start = filters.startDate ? startOfDay(new Date(filters.startDate)) : null;
        const end = filters.endDate ? endOfDay(new Date(filters.endDate)) : null;
        
        if (start && end) {
          q = query(q, where('date', '>=', Timestamp.fromDate(start)), 
                       where('date', '<=', Timestamp.fromDate(end)));
        } else if (start) {
          q = query(q, where('date', '>=', Timestamp.fromDate(start)));
        } else if (end) {
          q = query(q, where('date', '<=', Timestamp.fromDate(end)));
        }
      }
      
      const querySnapshot = await getDocs(q);
      const transactionsData = [];
      
      querySnapshot.forEach((doc) => {
        transactionsData.push({ id: doc.id, ...doc.data() });
      });
      
      // Apply reference filter client-side
      let filteredData = transactionsData;
      if (filters.reference) {
        const refLower = filters.reference.toLowerCase();
        filteredData = transactionsData.filter(tx => 
          tx.reference && tx.reference.toLowerCase().includes(refLower)
        );
      }
      
      setTransactions(filteredData);
    } catch (error) {
      console.error('Error fetching money in transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveTransaction = async () => {
    if (!formData.reference || !formData.amount || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }
  
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
  
    setIsSaving(true);
    
    try {
      // Prepare transaction data with contact information
      const transactionData = {
        ...formData,
        amount: amount,
        date: Timestamp.fromDate(new Date(formData.date)),
        updatedAt: Timestamp.now(),
        // Include the full contact object
        contact: formData.contact ? {
          id: formData.contact.id,
          name: formData.contact.name,
          company: formData.contact.company || '',
          email: formData.contact.email || '',
          phone: formData.contact.phone || '',
          address: formData.contact.address || ''
        } : null
      };

      if (isEditMode && editingId) {
        await updateDoc(doc(db, 'companies', companyId, 'moneyIn', editingId), transactionData);
      } else {
        transactionData.createdAt = Timestamp.now();
        transactionData.createdBy = user.uid;
        
        const refQuery = query(
          collection(db, 'companies', companyId, 'moneyIn'),
          where('reference', '==', formData.reference.trim())
        );
        
        const refSnapshot = await getDocs(refQuery);
        if (!refSnapshot.empty && !isEditMode) {
          alert('A transaction with this reference number already exists');
          setIsSaving(false);
          return;
        }
        
        await addDoc(collection(db, 'companies', companyId, 'moneyIn'), transactionData);
      }
      
      resetForm();
      fetchTransactions();
      
    } catch (error) {
      console.error('Error saving transaction:', error);
      alert('Failed to save transaction');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle edit transaction
  const handleEdit = (transaction) => {
    setFormData({
      date: transaction.date?.toDate ? format(transaction.date.toDate(), 'yyyy-MM-dd') : today,
      reference: transaction.reference || '',
      contact: transaction.contact || null, // Set the full contact object
      description: transaction.description || '',
      amount: transaction.amount?.toString() || '',
      category: transaction.category || 'revenue',
      paymentMethod: transaction.paymentMethod || 'cash',
      account: transaction.account || 'cash',
    });
    setIsEditMode(true);
    setEditingId(transaction.id);
    setShowForm(true);
    
    // Set the contact search to the contact's name if exists
    if (transaction.contact?.name) {
      setContactSearch(transaction.contact.name);
    } else {
      setContactSearch('');
    }
  }; 

  // Handle delete transaction
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }
    
    if (!user?.uid || !companyId) return;
    
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'moneyIn', id));
      fetchTransactions();
      alert('Transaction deleted successfully');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction');
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: today,
      endDate: today,
      reference: '',
    });
    fetchTransactions();
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      contact: null,
      description: '',
      amount: '',
      category: 'revenue',
      paymentMethod: 'cash',
      account: 'cash',
    });
    setIsEditMode(false);
    setEditingId(null);
    setShowForm(false);
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchTransactions();
  }, [user, filters.startDate, filters.endDate]);

  return (
    <div className="text-white md:px-6">
      <div className="flex items-center mb-4 gap-2">
        <button 
          onClick={() => navigate('/accounting')} 
          className="text-sm flex items-center gap-1 text-gray-300 hover:text-white"
        >
          <span className="text-lg">←</span> Back
        </button>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Money In</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
        >
          <FiPlus /> Record Money In
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-300">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-300">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by reference..."
                value={filters.reference || ''}
                onChange={(e) => setFilters({ ...filters, reference: e.target.value })}
                className="w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={fetchTransactions}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <FiSearch size={16} /> Search
              </button>
              <button
                onClick={resetFilters}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center justify-center gap-2"
              >
                <FiRefreshCw size={16} /> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Reference</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Account</th>
                <th className="sr-only">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-gray-900 divide-y divide-gray-700">
            {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-400">
                    No money in transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {transaction.date?.toDate ? format(transaction.date.toDate(), 'dd/MM/yyyy') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {transaction.reference || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {transaction.contact?.company || transaction.contact?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate">
                      {transaction.description || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-400 font-medium">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">
                      {transaction.category || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">
                      {transaction.account || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(transaction)}
                        className="text-purple-400 hover:text-purple-300 mr-3"
                        title="Edit"
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(transaction.id)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {transactions.length > 0 && (
              <tfoot className="bg-gray-800">
                <tr>
                  <th colSpan="4" className="px-6 py-3 text-right text-sm font-medium text-gray-300">
                    Total:
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-green-400">
                    {formatCurrency(transactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0))}
                  </th>
                  <th colSpan="4"></th>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Money In Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {isEditMode ? 'Edit Money In' : 'Record Money In'}
                </h2>
                <button 
                  onClick={resetForm}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reference *</label>
                  <input
                    type="text"
                    name="reference"
                    value={formData.reference}
                    onChange={handleInputChange}
                    placeholder="e.g. MIN-001"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    readOnly={isEditMode}
                    style={isEditMode ? { backgroundColor: '#374151', cursor: 'not-allowed' } : {}}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter a description for this transaction"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Contact *</label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search contacts..."
                        value={formData.contact?.name || contactSearch}
                        onChange={(e) => {
                          setContactSearch(e.target.value);
                          if (!e.target.value) {
                            setFormData(prev => ({
                              ...prev,
                              contact: null
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {formData.contact && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              contact: null
                            }));
                            setContactSearch('');
                          }}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    {contactSearch && !formData.contact && (
                      <div className="absolute z-10 mt-1 w-full bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredContacts.length > 0 ? (
                          filteredContacts.map(contact => (
                            <div 
                              key={contact.id}
                              className="px-4 py-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-0"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  contact: {
                                    id: contact.id,
                                    name: contact.name,
                                    company: contact.company || '',
                                    email: contact.email || '',
                                    phone: contact.phone || '',
                                    address: contact.address || ''
                                  }
                                }));
                                setContactSearch(contact.name);
                              }}
                            >
                              <div className="font-medium text-white">{contact.name}</div>
                              {contact.company && <div className="text-sm text-gray-300">{contact.company}</div>}
                              {contact.email && <div className="text-sm text-blue-400 truncate">{contact.email}</div>}
                              {contact.phone && <div className="text-sm text-gray-400">{contact.phone}</div>}
                              {contact.address && <div className="text-sm text-gray-400 truncate">{contact.address}</div>}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-400">No contacts found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="revenue">Sales Revenue</option>
                    <option value="loan">Loan Received</option>
                    <option value="investment">Investment</option>
                    <option value="refund">Refund</option>
                    <option value="other">Other Income</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Amount (MYR) *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-400">RM</span>
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0.01"
                      step="0.01"
                      className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-green-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Payment Method *</label>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="check">Check</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Deposit To *</label>
                  <select
                    name="account"
                    value={formData.account}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank Account</option>
                    <option value="credit_card">Credit Card</option>
                  </select>
                </div>
                
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveTransaction}
                  disabled={isSaving || !formData.reference || !formData.amount || !formData.category || !formData.contact}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : isEditMode ? (
                    'Update Transaction'
                  ) : (
                    'Record Money In'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoneyIn;